import { Link } from 'react-router-dom'
import type { ComponentType } from 'react'
import { DiscordIcon, SteamIcon } from '@/components/BrandIcons'
import serverConfig from '@/config/serverConfig'
import useCommunity from '@/hooks/useCommunity'
import { useLanguage } from '@/localization'

const socialIcons: Record<string, ComponentType<{ size?: number }>> = {
  discord: DiscordIcon,
  steam: SteamIcon,
}

const socialHover: Record<string, string> = {
  discord: 'hover:bg-grass-dark',
  steam: 'hover:bg-sky-deep',
}

const navLinks = [
  { to: '/servers', label: 'Servers' },
  { to: '/mods', label: 'Mods' },
  { to: '/join', label: 'How to Join' },
  { to: '/rules', label: 'Rules' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/#contact', label: 'Contact' },
  { to: '/stats', label: 'Stats' },
]

export default function Footer() {
  const { servers } = serverConfig
  const { social } = useCommunity()
  const { t } = useLanguage()

  return (
    <footer className="bg-slate-deep text-white/80 border-t-4 border-grass-dark mt-20">
      <div className="max-w-7xl mx-auto px-4 py-12 grid gap-10 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <img src="/brand/ul-logo.png" alt="Uliunai" className="w-10 h-10 object-contain" />
            <span className="font-pixel text-[11px] text-grass">ULIUNAI</span>
          </div>
          <p className="text-sm leading-relaxed">
            {t(
              'Survival and a curated modpack on Minecraft Java Edition. Friendly community, clear rules, active staff, and a welcoming crew.',
            )}
          </p>
        </div>

        <div>
          <h4 className="font-extrabold text-white mb-3">{t('Explore')}</h4>
          <ul className="space-y-2 text-sm">
            {navLinks.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-grass transition-colors">
                  {t(l.label)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-extrabold text-white mb-3">{t('Server Addresses')}</h4>
          <ul className="space-y-2 text-sm">
            <li>
              <span className="text-white/50">{t('Survival')}</span>
              <br />
              <span className="font-mono text-grass" data-no-translate>
                {servers.vanilla.host}
              </span>
            </li>
            <li>
              <span className="text-white/50">{t('Modded')}</span>
              <br />
              <span className="font-mono text-sky" data-no-translate>
                {servers.modded.host}
              </span>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-extrabold text-white mb-3">{t('Community')}</h4>
          <div className="flex gap-3 mb-4">
            {social.map((s) => {
              const Icon = socialIcons[s.icon] ?? DiscordIcon
              return (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-lg bg-white/10 ${socialHover[s.icon] ?? 'hover:bg-grass-dark'} transition-colors flex items-center justify-center`}
                  aria-label={s.label}
                >
                  <Icon size={18} />
                </a>
              )
            })}
          </div>
          <a
            href="https://uliunai.com/#community"
            className="text-sm text-white/60 hover:text-grass transition-colors"
          >
            {t('Part of the Uliunai community →')}
          </a>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <p className="text-white/50">
            {t('Copyright')} {new Date().getFullYear()} Uliunai.com.{' '}
            {t('All rights reserved.')} {t('Designed by')}{' '}
            <a
              href="https://litenweb.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-white/70 hover:text-white transition-colors"
            >
              Litenweb
            </a>
            .
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://uliunai.com/privacy"
              className="text-white/60 hover:text-grass transition-colors"
            >
              Privacy Policy
            </a>
            <a
              href="https://uliunai.com/terms"
              className="text-white/60 hover:text-grass transition-colors"
            >
              Terms of Service
            </a>
            <p>
              {t('Part of the')}{' '}
              <a
                href="https://uliunai.com"
                className="text-ul-red font-bold hover:underline"
              >
                {t('Uliunai community')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
